import { useGetGroupPurchasesDashboard } from '@shared/api/hooks/group';
import {
  GroupPurchasingDashboard,
  PurchasingDashboardDetailFood,
  PurchasingDashboardDetailPackaging,
} from '@shared/api/types';
import DualAxisAreaChart from '@shared/components/chart/dual-axis-area-chart';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import Loader from '@shared/components/loader';
import SimpleSelect from '@shared/components/simple-select';
import { Card } from '@shared/components/ui/card';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatTonnes } from '@shared/lib/format';
import { cn, formatNumber } from '@shared/lib/utils';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  BarLegend,
  ComparisonBarList,
  ComparisonBarRow,
  EmissionsBarList,
  EmissionsBarRow,
} from '../components/emissions-bar-list';
import GroupPage from '../components/group-page';
import { useGroupFilters } from '../hooks/use-group-filters';
import { usePeriodDateRange } from '../hooks/use-period-date-range';
import { buildOrgEmissionsSeries } from '../lib/org-emissions-series';

/**
 * Goods-type series colours — aligned with the brand chart tokens used
 * across the purchase / company report dashboards
 * (`src/components/reports/config.ts`) so Food & Drink reads teal-ish
 * and Packaging reads brand-orange across the platform.
 */
const GOODS_TYPE_COLORS = {
  foodDrink: 'var(--chart-food-and-drink)',
  packaging: 'var(--chart-packaging)',
};

const GOODS_TYPE_LEGEND = [
  { label: 'Food & Drink', color: GOODS_TYPE_COLORS.foodDrink },
  { label: 'Packaging', color: GOODS_TYPE_COLORS.packaging },
];

/** Emissions vs purchased-weight comparison colours/legend. */
const WEIGHT_COMPARISON_COLORS = {
  emissions: 'var(--chart-food-and-drink)',
  weight: 'var(--chart-weight)',
};

const WEIGHT_COMPARISON_LEGEND = [
  { label: 'Emissions (tCO₂e)', color: WEIGHT_COMPARISON_COLORS.emissions },
  {
    label: 'Purchased weight (tonnes)',
    color: WEIGHT_COMPARISON_COLORS.weight,
  },
];

const EMISSIONS_CHART_CONFIG = {
  totalEmissionsTonnes: {
    label: 'Emissions',
    color: 'var(--chart-food-and-drink)',
  },
} satisfies ChartConfig;

/** Stacked-area series for the "Emissions by goods type" variant. */
const GOODS_TYPE_CHART_CONFIG = {
  foodDrink: { label: 'Food & Drink', color: GOODS_TYPE_COLORS.foodDrink },
  packaging: { label: 'Packaging', color: GOODS_TYPE_COLORS.packaging },
} satisfies ChartConfig;

/** Left (filled area) axis for the "Emissions vs carbon intensity" variant. */
const INTENSITY_EMISSIONS_CHART_CONFIG = {
  totalEmissionsTonnes: {
    label: 'Emissions',
    color: 'var(--chart-food-and-drink)',
    unit: 'tCO2e',
  },
} satisfies ChartConfig;

/** Right (line) axis for the "Emissions vs carbon intensity" variant. */
const CARBON_INTENSITY_CHART_CONFIG = {
  carbonIntensity: {
    label: 'Carbon intensity',
    color: 'var(--chart-carbon-intensity)',
    unit: 'kgCO2e/kg',
  },
} satisfies ChartConfig;

const CARBON_INTENSITY_LEGEND = [
  { label: 'Emissions (tCO₂e)', color: 'var(--chart-food-and-drink)' },
  {
    label: 'Carbon intensity (kgCO₂e/kg)',
    color: 'var(--chart-carbon-intensity)',
  },
];

/** Variant options for the "Emissions over time" chart. */
const OVER_TIME_VARIANTS = {
  perMonth: 'Emissions per month',
  intensity: 'Emissions vs carbon intensity',
  goodsType: 'Emissions by goods type',
  byOrganisation: 'Emissions by organisation',
};

/** Variant options for the per-organisation / per-supplier bar sections. */
const BREAKDOWN_VARIANTS = {
  goodsType: 'Emissions by goods type',
  purchasedWeight: 'Emissions vs purchased weight',
};

type GoodsTypeDetails = {
  foodDrinkDetail: PurchasingDashboardDetailFood;
  packagingDetail: PurchasingDashboardDetailPackaging;
};

/** Food + packaging emissions for a breakdown row; null when neither reported. */
function combinedEmissionsTonnes(details: GoodsTypeDetails): number | null {
  const food = details.foodDrinkDetail.totalEmissionsTonnes;
  const packaging = details.packagingDetail.totalEmissionsTonnes;
  if (food == null && packaging == null) return null;
  return (food ?? 0) + (packaging ?? 0);
}

/** Food + packaging purchased weight for a breakdown row; null when neither reported. */
function combinedWeightKg(details: GoodsTypeDetails): number | null {
  const food = details.foodDrinkDetail.totalWeightKg;
  const packaging = details.packagingDetail.totalWeightKg;
  if (food == null && packaging == null) return null;
  return (food ?? 0) + (packaging ?? 0);
}

/** Carbon intensity (kgCO₂e/kg) from combined emissions and weight; null when not derivable. */
function intensityPerKg(details: GoodsTypeDetails): number | null {
  const emissions = combinedEmissionsTonnes(details);
  const weight = combinedWeightKg(details);
  if (emissions == null || weight == null || weight <= 0) return null;
  return (emissions * 1000) / weight;
}

export default function GroupPurchasingEmissionsPage() {
  const [{ orgs }] = useGroupFilters();
  const { start, end } = usePeriodDateRange();
  const { data, status, isError } = useGetGroupPurchasesDashboard({
    organizations: orgs.length ? orgs : undefined,
    dateStart: start ?? undefined,
    dateEnd: end ?? undefined,
  });

  if (isError) throw new Error('Failed to load purchasing emissions dashboard');

  return (
    <GroupPage
      pageKey="purchasingEmissions"
      lastRefreshedAt={data?.lastRefreshedAt}
    >
      {status === 'loading' && <Loader className="h-96" />}
      {!!data && (
        <div className="flex flex-col gap-8 pb-12">
          <HeroStats data={data} />
          <EmissionsOverTimeSection data={data} />
          <EmissionsByOrganisationSection data={data} />
          <EmissionsBySupplierSection data={data} />
        </div>
      )}
    </GroupPage>
  );
}

function HeroStats({ data }: { data: GroupPurchasingDashboard }) {
  const averageIntensity =
    data.totalEmissionsTonnes != null &&
    data.totalWeightKg != null &&
    data.totalWeightKg > 0
      ? (data.totalEmissionsTonnes * 1000) / data.totalWeightKg
      : null;

  return (
    <DashboardGrid>
      <MetricCard
        title="Total emissions"
        value={
          data.totalEmissionsTonnes != null
            ? formatNumber(data.totalEmissionsTonnes)
            : '-'
        }
        unit={data.totalEmissionsTonnes != null ? 'tCO₂e' : undefined}
        description="From purchased food, drinks and packaging"
      />
      <MetricCard
        title="Total weight purchased"
        value={
          data.totalWeightKg != null
            ? formatNumber(data.totalWeightKg / 1000)
            : '-'
        }
        unit={data.totalWeightKg != null ? 'tonnes' : undefined}
        description={`Across ${data.totalItemCount.toLocaleString()} purchased items`}
      />
      <MetricCard
        title="Overall carbon intensity"
        value={averageIntensity != null ? averageIntensity.toFixed(2) : '-'}
        unit={averageIntensity != null ? 'kgCO₂e/kg' : undefined}
        description="Emissions per kilogram purchased"
      />
    </DashboardGrid>
  );
}

function EmissionsOverTimeSection({
  data,
}: {
  data: GroupPurchasingDashboard;
}) {
  const [variant, setVariant] = useState(OVER_TIME_VARIANTS.perMonth);
  const isIntensity = variant === OVER_TIME_VARIANTS.intensity;
  const isGoodsType = variant === OVER_TIME_VARIANTS.goodsType;
  const isByOrganisation = variant === OVER_TIME_VARIANTS.byOrganisation;

  const sortedPeriods = useMemo(
    () =>
      [...data.emissionsOverTime].sort((a, b) =>
        a.periodEndDate.localeCompare(b.periodEndDate)
      ),
    [data.emissionsOverTime]
  );

  const chartData = useMemo(
    () =>
      sortedPeriods.map((point) => ({
        label: format(parseISO(point.periodEndDate), 'MMM yy'),
        totalEmissionsTonnes: point.totalEmissionsTonnes,
        foodDrink: point.foodDrinkDetail.totalEmissionsTonnes,
        packaging: point.packagingDetail.totalEmissionsTonnes,
        carbonIntensity: point.emissionFactor,
      })),
    [sortedPeriods]
  );

  const orgSeries = useMemo(
    () =>
      buildOrgEmissionsSeries(
        sortedPeriods.map((point) => ({
          label: format(parseISO(point.periodEndDate), 'MMM yy'),
          organisations: point.organisations,
        }))
      ),
    [sortedPeriods]
  );

  const yAxisProps = {
    allowDecimals: false,
    tickFormatter: (value: number) =>
      value.toLocaleString('en-GB', { maximumFractionDigits: 0 }),
    width: 44,
    tickMargin: 4,
  };

  const margin = { top: 8, right: 24, left: 0, bottom: 0 };

  return (
    <DashboardSection
      title="Emissions over time"
      description="Explore your purchasing emissions over time. You can split by goods type and organisation, or contrast with carbon intensity."
      actions={
        <SimpleSelect
          className="w-64"
          options={Object.values(OVER_TIME_VARIANTS)}
          value={variant}
          onValueChange={setVariant}
        />
      }
    >
      <Card className="flex flex-col gap-2 p-5">
        {isGoodsType && <BarLegend items={GOODS_TYPE_LEGEND} />}
        {isIntensity && <BarLegend items={CARBON_INTENSITY_LEGEND} />}
        {isByOrganisation && <BarLegend items={orgSeries.legend} />}
        {/*
          Every chart component stays mounted and overlaid; we toggle visibility
          rather than conditionally rendering one or the other. Swapping between
          DualAxisAreaChart and SimpleAreaChart unmounts/remounts Recharts'
          ResponsiveContainer, which paints blank until it re-measures — using
          `invisible` keeps each chart's box dimensions so no re-measure occurs.
        */}
        <div className="relative h-80 w-full">
          <div className={cn('absolute inset-0', { invisible: !isIntensity })}>
            <DualAxisAreaChart
              leftAxisConfig={INTENSITY_EMISSIONS_CHART_CONFIG}
              rightAxisConfig={CARBON_INTENSITY_CHART_CONFIG}
              chartData={chartData}
              className="aspect-auto size-full"
              margin={margin}
              nameKey="label"
              dataKey="label"
              fillOpacity={0.2}
              dot
              LeftYAxisProps={yAxisProps}
              RightYAxisProps={{
                tickFormatter: (value: number) => value.toFixed(2),
                width: 56,
              }}
            />
          </div>
          <div
            className={cn('absolute inset-0', {
              invisible: isIntensity || isByOrganisation,
            })}
          >
            <SimpleAreaChart
              chartConfig={
                isGoodsType ? GOODS_TYPE_CHART_CONFIG : EMISSIONS_CHART_CONFIG
              }
              chartData={chartData}
              className="aspect-auto size-full"
              margin={margin}
              nameKey="label"
              dataKey="totalEmissionsTonnes"
              stacked={isGoodsType}
              dot={!isGoodsType}
              strokeWidth={2}
              fillOpacity={isGoodsType ? 0.75 : 0.5}
              YAxisProps={yAxisProps}
              tooltipValueFormatter={(value) => formatTonnes(value)}
            />
          </div>
          <div
            className={cn('absolute inset-0', { invisible: !isByOrganisation })}
          >
            <SimpleAreaChart
              chartConfig={orgSeries.chartConfig}
              chartData={orgSeries.chartData}
              className="aspect-auto size-full"
              margin={margin}
              nameKey="label"
              dataKey="label"
              stacked
              reverseTooltipItems
              strokeWidth={2.5}
              fillOpacity={0.7}
              YAxisProps={yAxisProps}
              tooltipValueFormatter={(value) => formatTonnes(value)}
            />
          </div>
        </div>
      </Card>
    </DashboardSection>
  );
}

/**
 * Builds goods-type (stacked) and purchased-weight (comparison) row sets for a
 * breakdown section, sorted by total emissions descending. Shared by the
 * per-organisation and per-supplier sections.
 */
function useBreakdownRows<T extends GoodsTypeDetails>(
  items: readonly T[],
  getKey: (item: T) => string,
  getLabel: (item: T) => string
) {
  return useMemo(() => {
    const sorted = [...items].sort(
      (a, b) =>
        (combinedEmissionsTonnes(b) ?? 0) - (combinedEmissionsTonnes(a) ?? 0)
    );

    const goodsTypeRows: EmissionsBarRow[] = sorted.map((item) => {
      const total = combinedEmissionsTonnes(item);
      return {
        key: getKey(item),
        label: getLabel(item),
        segments: [
          {
            color: GOODS_TYPE_COLORS.foodDrink,
            tonnes: item.foodDrinkDetail.totalEmissionsTonnes,
          },
          {
            color: GOODS_TYPE_COLORS.packaging,
            tonnes: item.packagingDetail.totalEmissionsTonnes,
          },
        ],
        readout: total != null ? formatNumber(total) : null,
      };
    });

    const weightRows: ComparisonBarRow[] = sorted.map((item) => {
      const intensity = intensityPerKg(item);
      const weightKg = combinedWeightKg(item);
      return {
        key: getKey(item),
        label: getLabel(item),
        bars: [
          {
            color: WEIGHT_COMPARISON_COLORS.emissions,
            value: combinedEmissionsTonnes(item),
          },
          {
            color: WEIGHT_COMPARISON_COLORS.weight,
            // Convert to tonnes so tooltip readouts match the group-wide unit.
            value: weightKg != null ? weightKg / 1000 : null,
          },
        ],
        readout: intensity != null ? intensity.toFixed(2) : null,
        readoutUnit: 'kgCO₂e/kg',
        readoutCaption: 'intensity',
      };
    });

    return { goodsTypeRows, weightRows };
  }, [items, getKey, getLabel]);
}

/** Card body shared by both breakdown sections: legend + active variant's bar list. */
function BreakdownChart({
  variant,
  goodsTypeRows,
  weightRows,
}: {
  variant: string;
  goodsTypeRows: EmissionsBarRow[];
  weightRows: ComparisonBarRow[];
}) {
  const isWeight = variant === BREAKDOWN_VARIANTS.purchasedWeight;
  return (
    <Card className="flex flex-col gap-4 p-5">
      <BarLegend
        items={isWeight ? WEIGHT_COMPARISON_LEGEND : GOODS_TYPE_LEGEND}
      />
      {isWeight ? (
        <ComparisonBarList
          rows={weightRows}
          seriesNames={WEIGHT_COMPARISON_LEGEND.map((entry) => entry.label)}
        />
      ) : (
        <EmissionsBarList
          rows={goodsTypeRows}
          seriesNames={GOODS_TYPE_LEGEND.map((entry) => entry.label)}
        />
      )}
    </Card>
  );
}

function EmissionsByOrganisationSection({
  data,
}: {
  data: GroupPurchasingDashboard;
}) {
  const [variant, setVariant] = useState(BREAKDOWN_VARIANTS.goodsType);
  const { goodsTypeRows, weightRows } = useBreakdownRows(
    data.emissionsByOrganisation,
    (org) => org.organizationUuid,
    (org) => org.organizationName
  );

  return (
    <DashboardSection
      title="Emissions by organisation"
      description="Compare emissions across each of your organisations. You can split by goods type or contrast with purchased weight."
      actions={
        <SimpleSelect
          className="w-64"
          options={Object.values(BREAKDOWN_VARIANTS)}
          value={variant}
          onValueChange={setVariant}
        />
      }
    >
      <BreakdownChart
        variant={variant}
        goodsTypeRows={goodsTypeRows}
        weightRows={weightRows}
      />
    </DashboardSection>
  );
}

function EmissionsBySupplierSection({
  data,
}: {
  data: GroupPurchasingDashboard;
}) {
  const [variant, setVariant] = useState(BREAKDOWN_VARIANTS.goodsType);
  const { goodsTypeRows, weightRows } = useBreakdownRows(
    data.emissionsBySupplier,
    (supplier) => supplier.supplierName,
    (supplier) => supplier.supplierName
  );

  return (
    <DashboardSection
      title="Emissions by supplier"
      description="Compare emissions across each of your suppliers. You can split by goods type or contrast with purchased weight."
      actions={
        <SimpleSelect
          className="w-64"
          options={Object.values(BREAKDOWN_VARIANTS)}
          value={variant}
          onValueChange={setVariant}
        />
      }
    >
      <BreakdownChart
        variant={variant}
        goodsTypeRows={goodsTypeRows}
        weightRows={weightRows}
      />
    </DashboardSection>
  );
}
